import { Link, type RegisteredPage, useRoute } from '@buzola/router'
import { type RoleCondition, useProjectUserRoles } from '@contember/interface'
import { createContext } from '@contember/react-utils'
import { VariantProps } from 'class-variance-authority'
import { ChevronRight } from 'lucide-react'
import { Children, isValidElement, ReactNode, useMemo } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible'
import { SidebarMenu, SidebarMenuButton, sidebarMenuButtonVariants, SidebarMenuItem, SidebarMenuSub } from './sidebar'

export const MenuSection = ({ label, children }: { label?: ReactNode; children: ReactNode }) => (
	<div className="flex flex-col gap-1 group-data-[collapsible=icon]:gap-0.5">
		{label && (
			<div className="px-3 pt-1.5 pb-1 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/70 group-data-[collapsible=icon]:hidden">
				{label}
			</div>
		)}
		{children}
	</div>
)

export type MenuItemProps = {
	icon?: ReactNode
	label: ReactNode
	to?: RegisteredPage
	children?: ReactNode
	role?: RoleCondition
	expandedByDefault?: boolean
} & VariantProps<typeof sidebarMenuButtonVariants>

interface MenuContextValue {
	level: number
}

const [MenuContext, useMenuContext] = createContext<MenuContextValue>('MenuContext', { level: 0 })

const useIsActiveMenuItem = (to?: RegisteredPage): boolean => {
	const { matches } = useRoute()

	if (!to) {
		return false
	}

	return matches.some(m => m.node.id === to)
}

const useHasActiveChild = (children: ReactNode) => {
	const { matches } = useRoute()

	return useMemo(() => {
		if (!children) return false

		const matchIds = new Set(matches.map(m => m.node.id))

		const checkChild = (child: ReactNode): boolean => {
			if (!isValidElement(child)) return false

			const { to, children: grandChildren } = child.props as any

			if (to && matchIds.has(to)) return true

			if (!grandChildren) return false

			return Children.toArray(grandChildren).some(checkChild)
		}

		return Children.toArray(children).some(checkChild)
	}, [children, matches])
}

export const Menu = ({ children }: { children: ReactNode }) => {
	return (
		<MenuContext.Provider value={{ level: 0 }}>
			<SidebarMenu>
				{children}
			</SidebarMenu>
		</MenuContext.Provider>
	)
}

export const MenuItem = ({ icon, label, to, children, role, expandedByDefault, ...menuButtonProps }: MenuItemProps) => {
	const isActive = useIsActiveMenuItem(to)
	const hasActiveChild = useHasActiveChild(children)
	const projectRoles = useProjectUserRoles()
	const { level } = useMenuContext()

	if (role && !(typeof role === 'string' ? projectRoles.has(role) : role(projectRoles))) {
		return null
	}

	const hasChildren = Boolean(children)

	if (!hasChildren && to) {
		return (
			<SidebarMenuItem>
				<Link to={to} asChild>
					<SidebarMenuButton isActive={isActive} tooltip={label} {...menuButtonProps}>
						{icon}
						<span>{label}</span>
					</SidebarMenuButton>
				</Link>
			</SidebarMenuItem>
		)
	}

	return (
		<Collapsible defaultOpen={expandedByDefault || hasActiveChild} className="[&[data-state=open]>li>button>.menu-icon]:rotate-90">
			<SidebarMenuItem>
				<CollapsibleTrigger asChild>
					<SidebarMenuButton isActive={isActive} tooltip={label} {...menuButtonProps}>
						{icon}
						<span>{label}</span>
						{hasChildren && <ChevronRight className="menu-icon ml-auto !size-3.5 opacity-50 transition-transform duration-200" />}
					</SidebarMenuButton>
				</CollapsibleTrigger>
				{hasChildren && (
					<CollapsibleContent className="collapsible-animate">
						<SidebarMenuSub>
							<MenuContext.Provider value={{ level: level + 1 }}>
								{children}
							</MenuContext.Provider>
						</SidebarMenuSub>
					</CollapsibleContent>
				)}
			</SidebarMenuItem>
		</Collapsible>
	)
}
